import { authConfig } from '@/auth/auth.config'
import { deriveCodeChallenge, generateCodeVerifier, generateState } from '@/auth/pkce'
import { logger } from '@/logger'
import { apiClient } from './api.client'

/**
 * TOTP enrolment for privileged users (TO-63 privileged-mfa REQ-013, design D10).
 *
 * The enrolment runs a second authorization-code + PKCE flow against the
 * dedicated public enrolment client (scope `aws.cognito.signin.user.admin`),
 * whose access token — the "enrolment token" — the auth-service uses for the
 * user's own Cognito self-service MFA calls. The SPA exchanges the code at
 * Cognito directly (public client) and keeps the token in React state only:
 * nothing here ever writes a token to browser storage. Only the PKCE verifier
 * and `state` are stashed (not tokens), and the callback removes them.
 */

/** The sessionStorage key of the in-flight enrolment's PKCE verifier + state. */
export const MFA_ENROLMENT_STORAGE_KEY = 'mfa-enrol:pkce'

/** The SPA route Cognito redirects back to after the enrolment sign-in. */
export const MFA_ENROLMENT_CALLBACK_PATH = '/settings/security/mfa/callback'

/** The scopes granted to the enrolment client. */
export const MFA_ENROLMENT_SCOPE = 'openid aws.cognito.signin.user.admin'

/** The `secretCode` + `otpauth://` URI of a pending TOTP association. */
export interface MfaTotpSetup {
	secretCode: string
	otpauthUri: string
}

/** The exact redirect URI registered on the enrolment client. */
const redirectUri = (): string => `${window.location.origin}${MFA_ENROLMENT_CALLBACK_PATH}`

/** True when this stage has an enrolment client configured (design D2). */
export const isMfaEnrolmentAvailable = (): boolean => authConfig.mfaEnrolmentClientId !== ''

/**
 * Read and clear the stashed enrolment verifier + state. Clearing first makes
 * the callback single-use: a reload or replayed link finds nothing.
 */
export function consumeMfaEnrolmentPkce(): { verifier: string | null; state: string | null } {
	const raw = sessionStorage.getItem(MFA_ENROLMENT_STORAGE_KEY)
	sessionStorage.removeItem(MFA_ENROLMENT_STORAGE_KEY)
	try {
		const parsed = JSON.parse(raw ?? 'null') as { verifier?: unknown; state?: unknown } | null
		return {
			verifier: typeof parsed?.verifier === 'string' ? parsed.verifier : null,
			state: typeof parsed?.state === 'string' ? parsed.state : null,
		}
	} catch {
		return { verifier: null, state: null }
	}
}

export const mfaEnrolmentService = {
	/** Stash a fresh verifier + state and redirect to the enrolment client's `/oauth2/authorize`. */
	start: async (): Promise<void> => {
		// A missing enrolment client / domain makes `start()` build a dead
		// `/oauth2/authorize?client_id=` URL that silently bounces. The config
		// PRESENCE (never the id value) is logged so that failure is visible in the
		// browser console without having to reproduce it.
		logger.info('[mfa-enrol] starting enrolment redirect', {
			clientIdPresent: authConfig.mfaEnrolmentClientId !== '',
			domainPresent: Boolean(authConfig.domain),
			redirectUri: redirectUri(),
			available: isMfaEnrolmentAvailable(),
		})
		const verifier = generateCodeVerifier()
		const state = generateState()
		const challenge = await deriveCodeChallenge(verifier)
		sessionStorage.setItem(MFA_ENROLMENT_STORAGE_KEY, JSON.stringify({ verifier, state }))
		const params = new URLSearchParams({
			client_id: authConfig.mfaEnrolmentClientId,
			redirect_uri: redirectUri(),
			response_type: 'code',
			scope: MFA_ENROLMENT_SCOPE,
			code_challenge: challenge,
			code_challenge_method: 'S256',
			state,
		})
		window.location.assign(`https://${authConfig.domain}/oauth2/authorize?${params.toString()}`)
	},

	/** Exchange the authorization code at Cognito's `/oauth2/token`; returns the enrolment access token. */
	exchangeCode: async (input: { code: string; verifier: string }): Promise<string> => {
		const response = await fetch(`https://${authConfig.domain}/oauth2/token`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body: new URLSearchParams({
				grant_type: 'authorization_code',
				client_id: authConfig.mfaEnrolmentClientId,
				code: input.code,
				redirect_uri: redirectUri(),
				code_verifier: input.verifier,
			}).toString(),
		})
		if (!response.ok) {
			// A failed exchange (bad client config, expired code, wrong redirect URI)
			// is the point where the enrolment callback dies before any backend call.
			logger.warn('[mfa-enrol] code exchange failed', { status: response.status })
			throw new Error(`Enrolment code exchange failed (${response.status})`)
		}
		const body = (await response.json()) as { access_token?: unknown }
		if (typeof body.access_token !== 'string' || body.access_token === '') {
			logger.warn('[mfa-enrol] code exchange returned no access token')
			throw new Error('Enrolment code exchange returned no access token')
		}
		logger.info('[mfa-enrol] code exchange succeeded')
		return body.access_token
	},

	/** `POST /auth/mfa/totp/setup` — starts a TOTP association for the enrolment token's user. */
	setup: async (enrolmentToken: string): Promise<MfaTotpSetup> => {
		logger.debug('[mfa-enrol] POST /auth/mfa/totp/setup')
		const { data } = await apiClient.post<MfaTotpSetup>('/auth/mfa/totp/setup', { enrolmentToken })
		return data
	},

	/** `POST /auth/mfa/totp/verify` — confirms the association with a 6-digit code. */
	verify: async (enrolmentToken: string, code: string): Promise<{ mfaEnrolledAt: string }> => {
		logger.debug('[mfa-enrol] POST /auth/mfa/totp/verify')
		const { data } = await apiClient.post<{ mfaEnrolledAt: string }>('/auth/mfa/totp/verify', { enrolmentToken, code })
		logger.info('[mfa-enrol] verify succeeded; session will be revoked, re-auth expected')
		return data
	},
}
